/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { zodResolver } from '@hookform/resolvers/zod';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { useSWRConfig } from 'swr';
import * as z from 'zod';

import { Project } from '@/commons/protocols';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { useGet, useMutate } from '@/net/commons';

const nameSchema = z.object({
    name: z.string().min(3, { message: 'Project name must be at least 3 characters long' }),
});

const ProjectSettings = () => {
    const navigate = useNavigate();
    const { projectSlug } = useParams();
    const { mutate, cache } = useSWRConfig();
    const { trigger: updateTestSuiteSetting } = useMutate<Project>(`/bff/project/${projectSlug}/settings`, {
        revalidate: true,
        method: 'PATCH',
        onSuccess: (data: any, key: string) => {
            const keyToUpdate = `/bff/projects`;
            const cachedData = cache.get(keyToUpdate)?.data;
            if (cachedData) {
                const updatedData = cachedData.map((item: any) => (item.id === data.id ? { ...item, ...data } : item));
                mutate(keyToUpdate, updatedData);
            }
        },
    });

    const formName = useForm<z.infer<typeof nameSchema>>({
        defaultValues: {
            name: '',
        },
        resolver: zodResolver(nameSchema),
    });

    const [errorMessage, setErrorMessage] = useState('');
    const [shouldCheckName, setShouldCheckName] = useState(false);
    const { getValues } = formName;
    const { data: nameIsAvailable } = useGet<any>(
        shouldCheckName && getValues('name').length > 0
            ? `/bff/project/${encodeURIComponent(getValues('name'))}/check`
            : null,
        {
            method: 'GET',
        },
    );

    useEffect(() => {
        if (shouldCheckName && nameIsAvailable && nameIsAvailable?.available === false) {
            setErrorMessage('This name already exists.');
        } else {
            setErrorMessage('');
        }
    }, [nameIsAvailable, shouldCheckName]);

    // debounce time to not dos the server
    const handleInputChange = useCallback(
        debounce(() => {
            if (getValues('name').length > 0) {
                setShouldCheckName(true);
            }
        }, 300), // 300ms debounce time
        [],
    );

    const onNameSubmit = async (values: z.infer<typeof nameSchema>) => {
        try {
            await updateTestSuiteSetting({ name: values.name });
            toast({
                title: `Project renamed to ${values.name}`,
            });
            navigate(`/project/${values.name}/settings`);
        } catch (err) {
            const error = err as { info?: { error: string; code: string }; status: number };
            if (error.status === 409) {
                toast({
                    title: `Project name already exists. Please choose a different name.`,
                    variant: 'destructive',
                });
                return;
            }
            toast({
                title: `Error renaming project`,
                variant: 'destructive',
            });
        }
    };

    const handleNameSubmit = async (data: any) => {
        await onNameSubmit(data);
        formName.reset();
    };

    return (
        <div className="sm:max-w-6xl mx-auto">
            <h1 className="text-2xl font-medium text-gray-800">Settings</h1>
            <div className="mt-6" />
            <div className="rounded-xl border p-6 space-y-8">
                <div className="space-y-3">
                    <Form {...formName}>
                        <form onSubmit={formName.handleSubmit(handleNameSubmit)} className="flex flex-col gap-10">
                            <FormField
                                control={formName.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="flex flex-row gap-2 items-center">Project Name</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder={`${projectSlug}`}
                                                {...field}
                                                onInput={(e) => {
                                                    setErrorMessage('');
                                                    setShouldCheckName(false);
                                                    field.onChange(e);
                                                    handleInputChange();
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage>{errorMessage && errorMessage}</FormMessage>
                                    </FormItem>
                                )}
                            />
                            <div className="flex flex-row justify-end">
                                <Button
                                    type="submit"
                                    disabled={nameIsAvailable && nameIsAvailable?.available === false}
                                >
                                    Change
                                </Button>
                            </div>
                        </form>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default ProjectSettings;
